import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Send, Square, Sparkles, MessageSquare, RotateCcw, Wrench, History, Plus, X, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { MermaidBlock } from '../../components/nexus-ui/MermaidBlock';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Generate a persistent anonymous session ID
const getSessionId = (slug) => {
  const key = `consumer_session_${slug}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = `anon-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(key, id);
  }
  return id;
};

const ConsumerChat = () => {
  const { tenantSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [toolCalls, setToolCalls] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  const sessionId = useRef(getSessionId(tenantSlug)).current;

  // Fetch tenant config
  useEffect(() => {
    fetch(`${API_BASE}/api/consumer/page/${tenantSlug}`)
      .then(r => { if (!r.ok) throw new Error('Organization not found'); return r.json(); })
      .then(cfg => { setConfig(cfg); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [tenantSlug]);

  // Load conversation list
  const fetchConversations = useCallback(() => {
    fetch(`${API_BASE}/api/consumer/page/${tenantSlug}/conversations?session_id=${sessionId}&limit=20`)
      .then(r => r.ok ? r.json() : [])
      .then(setConversations)
      .catch(() => {});
  }, [tenantSlug, sessionId]);

  useEffect(() => { if (config) fetchConversations(); }, [config, fetchConversations]);

  // Load a specific conversation
  const loadConversation = useCallback((convId) => {
    fetch(`${API_BASE}/api/consumer/page/${tenantSlug}/conversations/${convId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.messages) {
          setMessages(data.messages.map(m => ({ role: m.role, content: m.content, tool_calls: m.tool_calls })));
          setConversationId(data.id);
          setSidebarOpen(false);
        }
      })
      .catch(() => {});
  }, [tenantSlug]);

  // Apply branding CSS vars
  useEffect(() => {
    if (!config?.branding) return;
    const root = document.documentElement;
    const b = config.branding;
    if (b.primary_color) root.style.setProperty('--consumer-primary', b.primary_color);
    if (b.accent_color) root.style.setProperty('--consumer-accent', b.accent_color);
    return () => {
      root.style.removeProperty('--consumer-primary');
      root.style.removeProperty('--consumer-accent');
    };
  }, [config]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 140) + 'px';
    }
  }, [input]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming) return;
    const text = input.trim();
    setInput('');
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);
    setStreamingContent('');
    setToolCalls([]);

    const allMessages = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
    const controller = new AbortController();
    abortRef.current = controller;
    // Hoisted so the AbortError handler can still see what streamed so far.
    let fullContent = '';
    let currentToolCalls = [];

    try {
      const resp = await fetch(`${API_BASE}/api/consumer/page/${tenantSlug}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: allMessages,
          stream: true,
          conversation_id: conversationId,
          session_id: sessionId,
        }),
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error('Chat request failed');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';
      let convId = conversationId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          if (raw === '[DONE]') break;
          try {
            const data = JSON.parse(raw);
            if (data.type === 'content') {
              fullContent += data.text;
              setStreamingContent(fullContent);
            } else if (data.type === 'tool_calls') {
              currentToolCalls = data.tool_calls || [];
              setToolCalls(currentToolCalls);
            } else if (data.type === 'done') {
              convId = data.conversation_id;
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }

      setMessages(prev => [...prev, { role: 'assistant', content: fullContent, tool_calls: currentToolCalls }]);
      setStreamingContent('');
      if (convId) setConversationId(convId);
      // Refresh conversation list
      setTimeout(fetchConversations, 500);
    } catch (err) {
      if (err.name === 'AbortError') {
        // User clicked Stop — keep whatever streamed so far as the (partial)
        // assistant turn instead of dropping it.
        if (fullContent) {
          setMessages(prev => [...prev, { role: 'assistant', content: fullContent + '\n\n_— stopped by you_', tool_calls: currentToolCalls, aborted: true }]);
        }
        setStreamingContent('');
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [input, isStreaming, messages, conversationId, tenantSlug, sessionId, fetchConversations]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setStreamingContent('');
    setToolCalls([]);
    setSidebarOpen(false);
    inputRef.current?.focus();
  };

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} />;

  const primaryColor = config?.branding?.primary_color || '#1E4E8C';
  const tenantName = config?.tenant_name || 'AI Assistant';
  const welcome = config?.branding?.welcome_message || `Welcome to ${tenantName}`;

  return (
    <div className="flex h-screen bg-nexus-bg-primary" data-testid="consumer-chat-page">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full border-r border-nexus-border-subtle bg-nexus-bg-secondary overflow-hidden flex-shrink-0"
            data-testid="consumer-sidebar"
          >
            <div className="w-[280px] h-full flex flex-col">
              <div className="p-3 border-b border-nexus-border-subtle">
                <button
                  onClick={handleNewChat}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-nexus-text-primary hover:bg-nexus-bg-tertiary/50 border border-nexus-border-subtle transition-colors"
                  style={{ borderColor: `${primaryColor}30` }}
                  data-testid="consumer-sidebar-new-chat"
                >
                  <Plus size={15} style={{ color: primaryColor }} />
                  New Chat
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {conversations.length === 0 ? (
                  <p className="text-xs text-nexus-text-tertiary text-center py-6">No conversations yet</p>
                ) : conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => loadConversation(conv.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-colors truncate ${
                      conversationId === conv.id
                        ? 'bg-white/[0.08] text-nexus-text-primary'
                        : 'text-nexus-text-secondary hover:bg-white/[0.04] hover:text-nexus-text-secondary'
                    }`}
                    data-testid={`consumer-conv-${conv.id}`}
                  >
                    <span className="block truncate font-medium">{conv.title || 'Untitled'}</span>
                    <span className="block text-[10px] text-nexus-text-tertiary mt-0.5">
                      {new Date(conv.updated_at).toLocaleDateString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="h-14 flex items-center px-4 md:px-6 border-b border-nexus-border-subtle flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1F2937 100%)' }}
        >
          <button
            onClick={() => setSidebarOpen(prev => !prev)}
            className="p-2 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors mr-2"
            data-testid="consumer-toggle-sidebar"
          >
            {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
          </button>
          <div className="flex items-center gap-3 flex-1">
            {config?.branding?.logo_url ? (
              <img src={config.branding.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: primaryColor }}>
                <span className="text-white text-xs font-bold">{tenantName[0]}</span>
              </div>
            )}
            <div>
              <h1 className="text-sm font-semibold text-white" data-testid="consumer-chat-title">{tenantName}</h1>
              <p className="text-[10px]" style={{ color: primaryColor }}>Online</p>
            </div>
          </div>
          <button
            onClick={handleNewChat}
            className="p-2 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors"
            data-testid="consumer-new-chat-btn"
          >
            <RotateCcw size={16} />
          </button>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6" data-testid="consumer-chat-messages">
          {messages.length === 0 && !isStreaming ? (
            <ConsumerEmptyState
              tenantName={tenantName}
              welcome={welcome}
              primaryColor={primaryColor}
              onPromptClick={(p) => { setInput(p); inputRef.current?.focus(); }}
            />
          ) : (
            <div className="max-w-2xl mx-auto space-y-5">
              {messages.map((msg, i) => (
                <ConsumerBubble key={i} msg={msg} primaryColor={primaryColor} />
              ))}
              {isStreaming && streamingContent && (
                <ConsumerBubble msg={{ role: 'assistant', content: streamingContent }} primaryColor={primaryColor} isStreaming />
              )}
              {isStreaming && toolCalls.length > 0 && !streamingContent && (
                <ToolCallIndicator toolCalls={toolCalls} primaryColor={primaryColor} />
              )}
              {isStreaming && !streamingContent && toolCalls.length === 0 && (
                <TypingDots primaryColor={primaryColor} />
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-4 md:px-6 pb-4 pt-2 border-t border-nexus-border-subtle">
          <div className="max-w-2xl mx-auto">
            <div className="relative bg-nexus-bg-tertiary border border-nexus-border-subtle rounded-2xl focus-within:border-opacity-30 transition-colors"
              style={{ '--tw-ring-color': primaryColor }}>
              <textarea
                ref={(el) => { textareaRef.current = el; inputRef.current = el; }}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={config?.strings?.placeholder || 'Type your message...'}
                disabled={isStreaming}
                rows={1}
                className="w-full bg-transparent px-4 py-3 pr-14 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary resize-none focus:outline-none"
                style={{ minHeight: '48px', maxHeight: '140px' }}
                data-testid="consumer-chat-input"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.92 }}
                onClick={isStreaming ? () => abortRef.current?.abort() : handleSend}
                disabled={!isStreaming && !input.trim()}
                className="absolute right-2 bottom-2 p-2.5 rounded-xl text-white disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                style={{ background: isStreaming ? '#ef4444' : primaryColor }}
                title={isStreaming ? 'Stop generation' : 'Send'}
                data-testid={isStreaming ? 'consumer-stop-btn' : 'consumer-send-btn'}
              >
                {isStreaming ? <Square size={16} fill="currentColor" /> : <Send size={16} />}
              </motion.button>
            </div>
            <p className="text-[10px] text-nexus-text-tertiary text-center mt-2 select-none">
              {config?.strings?.powered_by || 'Powered by Cognara'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConsumerBubble = ({ msg, primaryColor, isStreaming }) => {
  const isUser = msg.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`consumer-msg-${msg.role}`}
    >
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 ${
        isUser ? 'bg-white/10' : ''
      }`} style={!isUser ? { background: primaryColor } : {}}>
        {isUser
          ? <span className="text-nexus-text-secondary text-[10px] font-bold">U</span>
          : <MessageSquare size={13} className="text-white" />
        }
      </div>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
        isUser
          ? 'bg-white/[0.08] text-nexus-text-primary rounded-tr-sm'
          : 'bg-nexus-bg-tertiary border border-nexus-border-subtle text-nexus-text-primary rounded-tl-sm'
      }`}>
        {msg.content ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}
            components={{
              code({ className, children }) {
                const isInline = !className;
                if (isInline) return <code className="px-1 py-0.5 bg-nexus-bg-tertiary/50 rounded text-xs font-mono" style={{ color: primaryColor }}>{children}</code>;
                const lang = className?.replace('language-', '') || '';
                if (lang === 'mermaid') return <MermaidBlock code={String(children).replace(/\n$/, '')} />;
                return <pre className="bg-black/30 rounded-lg p-3 my-2 overflow-x-auto text-xs"><code>{children}</code></pre>;
              },
              p({ children }) { return <p className="mb-1.5 last:mb-0">{children}</p>; },
              ul({ children }) { return <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>; },
              ol({ children }) { return <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>; },
              li({ children }) { return <li className="text-nexus-text-primary">{children}</li>; },
              h1({ children }) { return <h1 className="text-base font-bold mt-3 mb-1.5">{children}</h1>; },
              h2({ children }) { return <h2 className="text-sm font-semibold mt-2.5 mb-1">{children}</h2>; },
              h3({ children }) { return <h3 className="text-sm font-medium mt-2 mb-1">{children}</h3>; },
            }}
          >{msg.content}</ReactMarkdown>
        ) : null}
        {isStreaming && (
          <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.7, repeat: Infinity, repeatType: 'reverse' }}
            className="inline-block w-[2px] h-[14px] ml-0.5 align-text-bottom" style={{ background: primaryColor }} />
        )}
        {msg.tool_calls && msg.tool_calls.length > 0 && (
          <div className="mt-2 space-y-1">
            {msg.tool_calls.map((tc, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px] text-nexus-text-tertiary">
                <Wrench size={10} style={{ color: primaryColor }} />
                <span>Used <span className="font-medium text-nexus-text-secondary">{tc.tool}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ToolCallIndicator = ({ toolCalls, primaryColor }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2.5">
    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: primaryColor }}>
      <Wrench size={13} className="text-white" />
    </div>
    <div className="bg-nexus-bg-tertiary border border-nexus-border-subtle rounded-2xl rounded-tl-sm px-4 py-2.5">
      {toolCalls.map((tc, i) => (
        <div key={i} className="flex items-center gap-2 text-[12px] text-nexus-text-secondary">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
            <Wrench size={12} style={{ color: primaryColor }} />
          </motion.div>
          <span>Calling <span className="font-medium text-nexus-text-secondary">{tc.tool}</span>...</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const TypingDots = ({ primaryColor }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2.5">
    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: primaryColor }}>
      <MessageSquare size={13} className="text-white" />
    </div>
    <div className="bg-nexus-bg-tertiary border border-nexus-border-subtle rounded-2xl rounded-tl-sm px-4 py-3">
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => (
          <motion.span key={i} animate={{ y: [0, -6, 0] }}
            transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.1, ease: 'easeInOut' }}
            className="w-1.5 h-1.5 rounded-full" style={{ background: primaryColor }} />
        ))}
      </div>
    </div>
  </motion.div>
);

const ConsumerEmptyState = ({ tenantName, welcome, primaryColor, onPromptClick }) => {
  const prompts = ['What can you help me with?', 'Tell me about your services', 'How do I get started?', 'Show me an example'];
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center max-w-md">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg"
          style={{ background: primaryColor, boxShadow: `0 8px 32px ${primaryColor}30` }}>
          <MessageSquare size={24} className="text-white" />
        </motion.div>
        <motion.h2 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="text-lg font-semibold text-white" data-testid="consumer-welcome-title">{welcome}</motion.h2>
        <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="text-nexus-text-tertiary mt-1.5 text-sm">Ask anything to get started</motion.p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {prompts.map((p, i) => (
            <motion.button key={p} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.05 }}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => onPromptClick(p)}
              className="flex items-center gap-2 px-3 py-2 text-xs text-left text-nexus-text-secondary bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-xl hover:bg-nexus-bg-tertiary/50 hover:border-white/10 transition-colors"
              data-testid={`consumer-prompt-${i}`}>
              <Sparkles size={12} style={{ color: primaryColor }} className="flex-shrink-0" />
              {p}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

const LoadingScreen = () => (
  <div className="flex items-center justify-center h-screen bg-nexus-bg-primary">
    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
      className="w-8 h-8 border-2 border-white/10 border-t-[#1E4E8C] rounded-full" />
  </div>
);

const ErrorScreen = ({ message }) => (
  <div className="flex items-center justify-center h-screen bg-nexus-bg-primary">
    <div className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-3">
        <MessageSquare size={24} className="text-red-400" />
      </div>
      <h2 className="text-lg font-semibold text-white mb-1" data-testid="consumer-error-title">Page Not Found</h2>
      <p className="text-nexus-text-tertiary text-sm">{message}</p>
    </div>
  </div>
);

export default ConsumerChat;
